// @spec GHG-STR-012
/**
 * React hook for uploading GHG documents for OCR processing
 */

import { useAuth } from '@clerk/clerk-react';
import { useCallback, useState } from 'react';

import { useOrganizationId } from '@/hooks/use-organization-id';

import type { GhgDocumentType, GhgOcrUploadResponse } from '../types/ocr';

import { uploadGhgOcrDocument } from './ocr';

interface UseGhgOcrUploadState {
  isUploading: boolean;
  progress: number;
  error: Error | null;
  data: GhgOcrUploadResponse | null;
}

interface UseGhgOcrUploadResult extends UseGhgOcrUploadState {
  upload: (
    file: File,
    documentType: GhgDocumentType,
    facilityId?: string,
    reportingPeriodId?: string
  ) => Promise<GhgOcrUploadResponse | null>;
  reset: () => void;
}

/**
 * Hook for uploading GHG documents with progress tracking.
 */
export function useGhgOcrUpload(): UseGhgOcrUploadResult {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();

  const [state, setState] = useState<UseGhgOcrUploadState>({
    isUploading: false,
    progress: 0,
    error: null,
    data: null,
  });

  const upload = useCallback(
    async (
      file: File,
      documentType: GhgDocumentType,
      facilityId?: string,
      reportingPeriodId?: string
    ): Promise<GhgOcrUploadResponse | null> => {
      if (!organizationId) {
        setState((prev) => ({
          ...prev,
          error: new Error('Organization not found'),
        }));
        return null;
      }

      setState({
        isUploading: true,
        progress: 0,
        error: null,
        data: null,
      });

      try {
        const result = await uploadGhgOcrDocument(
          getToken,
          organizationId,
          file,
          documentType,
          facilityId,
          reportingPeriodId,
          (progress) => {
            setState((prev) => ({
              ...prev,
              progress,
            }));
          }
        );

        setState({
          isUploading: false,
          progress: 100,
          error: null,
          data: result,
        });

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed');
        setState({
          isUploading: false,
          progress: 0,
          error,
          data: null,
        });
        return null;
      }
    },
    [getToken, organizationId]
  );

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      progress: 0,
      error: null,
      data: null,
    });
  }, []);

  return {
    ...state,
    upload,
    reset,
  };
}
